import React, { useState, useEffect, useCallback } from 'react';
import API from '../api/config';
import '../styles/Questions.css';
import MathRenderer, { hasMath } from '../components/MathRenderer';

function MathOrText({ text, subjectId, block = false, className = '' }) {
  if (!text) return null;
  if (hasMath(text, subjectId)) return <MathRenderer text={text} subjectId={subjectId} block={block} className={className} />;
  return <span className={className}>{text}</span>;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2004 + 1 }, (_, i) => CURRENT_YEAR - i);

const DEFAULT_FILTER = { subject_id: '', kcet_year: '2004', module_id: '' };
const EMPTY_FORM   = {
  subject_id: '', module_id: '', question_text: '',
  option_a: '', option_b: '', option_c: '', option_d: '',
  correct_answer: 'a', explanation: '', kcet_year: '',
};

export default function Questions() {
  const [questions,  setQuestions]  = useState([]);
  const [subjects,   setSubjects]   = useState([]);
  const [modules,    setModules]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showModal,  setShowModal]  = useState(false);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState(DEFAULT_FILTER);
  const [form,       setForm]       = useState(EMPTY_FORM);

  /* ── Load subjects ── */
  useEffect(() => {
    API.get('/api/subjects')
      .then(r => {
        const rows = Array.isArray(r.data) ? r.data : [];
        setSubjects(rows);
        if (rows.length > 0) setForm(f => ({ ...f, subject_id: rows[0].subject_id }));
      })
      .catch(() => {});
  }, []);

  /* ── Load modules ── */
  useEffect(() => {
    API.get('/api/subjects/modules')
      .then(r => setModules(Array.isArray(r.data) ? r.data : []))
      .catch(() => setModules([]));
  }, []);

  /* ── Fetch questions ── */
  const fetchQuestions = useCallback(async (activeFilter = filter) => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilter.subject_id) params.subject_id = activeFilter.subject_id;
      if (activeFilter.kcet_year)  { params.kcet_year = activeFilter.kcet_year; params.exam_year = activeFilter.kcet_year; }
      if (activeFilter.module_id)  params.module_id  = activeFilter.module_id;
      const res = await API.get('/api/questions', { params });
      const data = Array.isArray(res.data) ? res.data : [];
      const subjectOrder = { Physics: 1, Chemistry: 2, Mathematics: 3 };
      setQuestions(data.sort((a, b) => {
        const sa = subjectOrder[a.subject_name] ?? 99;
        const sb = subjectOrder[b.subject_name] ?? 99;
        if (sa !== sb) return sa - sb;
        return (a.question_number || a.question_id) - (b.question_number || b.question_id);
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchQuestions(DEFAULT_FILTER); }, []); // eslint-disable-line

  const handleFilterChange = e => {
    const { name, value } = e.target;
    const updated = { ...filter, [name]: value };
    setFilter(updated);
    fetchQuestions(updated);
  };

  const handleSearch = () => fetchQuestions(filter);

  const handleClear = () => {
    setFilter(DEFAULT_FILTER);
    setSearch('');
    fetchQuestions(DEFAULT_FILTER);
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCreate = async e => {
    e.preventDefault();
    setError('');
    try {
      await API.post('/api/questions', {
        ...form,
        module_id:        form.module_id || null,
        difficulty_level: 'medium',
        tags:             '',
      });
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchQuestions(filter);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add question');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await API.delete(`/api/questions/${id}`);
      fetchQuestions(filter);
    } catch (err) { console.error(err); }
  };

  const displayed = questions.filter(q => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (q.question_text || '').toLowerCase().includes(s) ||
      (q.subject_name  || '').toLowerCase().includes(s) ||
      String(q.kcet_year  || '').includes(s) ||
      String(q.exam_year  || '').includes(s)
    );
  });

  const filteredModules = filter.subject_id
    ? modules.filter(m => !m.subject_id || String(m.subject_id) === String(filter.subject_id))
    : modules;

  const formModules = form.subject_id
    ? modules.filter(m => !m.subject_id || String(m.subject_id) === String(form.subject_id))
    : modules;

  const hasFilters = filter.subject_id || filter.module_id || search ||
    (filter.kcet_year && filter.kcet_year !== DEFAULT_FILTER.kcet_year);

  return (
    <div className="page-container">

      {/* ── Header ── */}
      <div className="qb-header">
        <div className="qb-header-left">
          <h1 className="qb-title">Question Bank</h1>
          <span className="qb-count-badge">{questions.length.toLocaleString()} questions</span>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>+ Add Question</button>
      </div>

      {/* ── Filter bar ── */}
      <div className="filter-bar">
        <div className="filter-search-wrap">
          <span className="filter-search-icon">🔍</span>
          <input
            className="filter-search-input"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search questions…"
          />
        </div>

        <select className="form-select filter-select" name="subject_id" value={filter.subject_id} onChange={handleFilterChange}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
        </select>

        <select className="form-select filter-select" name="module_id" value={filter.module_id} onChange={handleFilterChange}>
          <option value="">All Modules</option>
          {filteredModules.map(m => <option key={m.module_id} value={m.module_id}>{m.name || m.module_name}</option>)}
        </select>

        <select className="form-select filter-select" name="kcet_year" value={filter.kcet_year} onChange={handleFilterChange}>
          <option value="">All Years</option>
          {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <button className="btn-primary btn-search" onClick={handleSearch}>Search</button>

        {hasFilters && (
          <button className="btn-reset" onClick={handleClear}>✕ Clear</button>
        )}
      </div>

      {/* Result count */}
      <div className="results-count">
        Showing <strong>{displayed.length}</strong> of <strong>{questions.length}</strong> question{questions.length !== 1 ? 's' : ''}
        {hasFilters && <span className="results-filtered-note"> (filtered)</span>}
      </div>

      {/* ── Table ── */}
      <div className="table-container">
        {loading ? (
          <div className="loading-text">Loading questions…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 64, whiteSpace: 'nowrap' }}>Sl. No.</th>
                <th className="qb-th-question">Question</th>
                <th style={{ width: 110 }}>Subject</th>
                <th style={{ width: 100 }}>Module</th>
                <th style={{ width: 60 }}>Year</th>
                <th style={{ width: 60, textAlign: 'center' }}>Ans</th>
                <th style={{ width: 110, textAlign: 'center' }}>Status</th>
                <th style={{ width: 80, textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    {questions.length === 0 ? '📭 No questions uploaded yet' : '🔍 No questions match your search'}
                  </td>
                </tr>
              ) : (
                displayed.map((q, i) => {
                  const year = q.exam_year || q.kcet_year || '—';
                  return (
                    <tr key={q.question_id}>
                      <td className="td-num">{q.question_number || (i + 1)}</td>
                      <td>
                        <div className="q-text">
                          <MathOrText text={q.question_text} subjectId={q.subject_id} block={false} />
                        </div>
                        <div className="q-opts">
                          {q.option_a && <span className="q-opt"><b>a)</b> <MathOrText text={q.option_a} subjectId={q.subject_id} /></span>}
                          {q.option_b && <span className="q-opt"><b>b)</b> <MathOrText text={q.option_b} subjectId={q.subject_id} /></span>}
                          {q.option_c && <span className="q-opt"><b>c)</b> <MathOrText text={q.option_c} subjectId={q.subject_id} /></span>}
                          {q.option_d && <span className="q-opt"><b>d)</b> <MathOrText text={q.option_d} subjectId={q.subject_id} /></span>}
                        </div>
                      </td>
                      <td><span className="badge badge-role">{q.subject_name || '—'}</span></td>
                      <td className="td-muted">{q.module_name || '—'}</td>
                      <td><span className="year-badge">{year}</span></td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge badge-active">{q.correct_answer?.toLowerCase()})</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {q.hod_approved
                          ? <span className="badge badge-approved">✅ Approved</span>
                          : q.is_verified
                            ? <span className="badge badge-verified">🔍 Verified</span>
                            : <span className="badge badge-pending">⏳ Pending</span>
                        }
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-toggle btn-deactivate"
                          onClick={() => handleDelete(q.question_id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Add Question Modal ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: 600 }}>
            <div className="modal-header-row">
              <h2 className="modal-title">Add New Question</h2>
              <button className="modal-close-btn" onClick={() => { setShowModal(false); setError(''); }}>✕</button>
            </div>
            {error && <div className="error-box">{error}</div>}

            <form onSubmit={handleCreate}>
              <div className="modal-row">
                <div className="form-field">
                  <label className="form-label">Subject</label>
                  <select className="form-select" name="subject_id" value={form.subject_id} onChange={handleChange}>
                    <option value="">— Select Subject —</option>
                    {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Year</label>
                  <select className="form-select" name="kcet_year" value={form.kcet_year} onChange={handleChange}>
                    <option value="">— Select Year —</option>
                    {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {formModules.length > 0 && (
                <div className="form-field">
                  <label className="form-label">Module</label>
                  <select className="form-select" name="module_id" value={form.module_id} onChange={handleChange}>
                    <option value="">— Select Module —</option>
                    {formModules.map(m => <option key={m.module_id} value={m.module_id}>{m.name || m.module_name}</option>)}
                  </select>
                </div>
              )}

              <div className="form-field">
                <label className="form-label">Question Text</label>
                <textarea className="form-input" name="question_text" value={form.question_text}
                  onChange={handleChange} rows="3" placeholder="Enter your question here…" required
                  style={{ resize: 'vertical' }} />
              </div>

              <div className="form-label" style={{ marginBottom: 10 }}>Answer Options</div>
              <div className="options-grid">
                {['a', 'b', 'c', 'd'].map(opt => (
                  <div className="opt-field" key={opt}>
                    <div className="opt-prefix">{opt})</div>
                    <input
                      className="form-input opt-input"
                      type="text"
                      name={`option_${opt}`}
                      value={form[`option_${opt}`]}
                      onChange={handleChange}
                      placeholder={`Option ${opt.toUpperCase()}`}
                      required
                    />
                  </div>
                ))}
              </div>

              <div className="form-field" style={{ maxWidth: 240 }}>
                <label className="form-label">Correct Answer</label>
                <select className="form-select" name="correct_answer" value={form.correct_answer} onChange={handleChange}>
                  <option value="a">a) Option A</option>
                  <option value="b">b) Option B</option>
                  <option value="c">c) Option C</option>
                  <option value="d">d) Option D</option>
                </select>
              </div>

              <div className="form-field">
                <label className="form-label">
                  Explanation <span className="form-label-hint">(optional)</span>
                </label>
                <textarea className="form-input" name="explanation" value={form.explanation}
                  onChange={handleChange} rows="2" placeholder="Explain why the answer is correct…"
                  style={{ resize: 'vertical' }} />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setError(''); }}>Cancel</button>
                <button type="submit" className="btn-submit">Add Question</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}